import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import axios from 'axios';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import Dashboard from './components/Dashboard';
import MessageViewer from './components/MessageViewer';
import RecipientEventView from './components/RecipientEventView';
import SupportAgent from './components/SupportAgent';
import { getAuthConfig } from './utils/auth';
import {
  clearDemoSessionData,
  isDemoSessionExpired,
  isDemoUser,
  normalizeDemoUser,
} from './utils/demoSession';

function AppContent() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  // Support Agent States
  const [agentOpen, setAgentOpen] = useState(false);
  const [agentMessages, setAgentMessages] = useState([
    { role: 'assistant', text: 'Hallo! 👋 Ich bin dein TimeLock-Assistent. Wie kann ich dir heute helfen?' }
  ]);
  const [agentInput, setAgentInput] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);

  const clearStoredSession = () => {
    localStorage.removeItem('timeLockToken');
    localStorage.removeItem('timeLockUser');
    clearDemoSessionData();
  };

  useEffect(() => {
    let isMounted = true;

    // Check URL for demo parameter (auto-login)
    const searchParams = new URLSearchParams(location.search);
    const demoParam = searchParams.get('demo');
    const isDemo = demoParam === 'start' || demoParam === 'demo';
    
    if (isDemo) {
      // Auto-login demo user with special demo package
      const demoUser = normalizeDemoUser({
        id: 'demo-user-' + Date.now(),
        email: 'demo@demo.demo',
        plan: 'demo',  // Use special demo package (3 messages, 2 events, 1 recipient)
        admin: 0,
        isDemo: true,  // Session-based flag - used for permission checks
      });
      localStorage.setItem('timeLockToken', 'demo-token-' + Date.now());
      localStorage.setItem('timeLockUser', JSON.stringify(demoUser));
      setIsLoggedIn(true);
      setUser(demoUser);
      setIsAdminMode(false);
      
      // Clean URL
      window.history.replaceState({}, document.title, '/');
      return;
    }

    // Check URL for auto-login via email+token (e.g. QR-code / magic-link login)
    const autoEmail = searchParams.get('login_email');
    const autoToken = searchParams.get('login_token');

    if (autoEmail && autoToken) {
      axios
        .post('/api/auth/user-login', { email: autoEmail, user_token: autoToken })
        .then((response) => {
          if (!isMounted || !response.data?.success) return;
          const { token, user: loggedInUser } = response.data;
          const normalizedUser = normalizeDemoUser(loggedInUser);
          localStorage.setItem('timeLockToken', token);
          localStorage.setItem('timeLockUser', JSON.stringify(normalizedUser));
          setIsLoggedIn(true);
          setUser(normalizedUser);
          setIsAdminMode(normalizedUser.admin === 1);
        })
        .catch((error) => {
          console.error('Auto-login failed:', error);
        })
        .finally(() => {
          if (isMounted) {
            window.history.replaceState({}, document.title, '/');
          }
        });
      return;
    }

    // Explicit "App Login" entry point (website nav link, /app/?login=1): a leftover
    // demo session should not be silently resumed here, since the user clicked
    // something meant to log in, not "continue demo". Real (non-demo) sessions are
    // left alone — staying logged in when revisiting /app/ is the desired behavior.
    const isExplicitLoginIntent = searchParams.get('login') === '1';
    if (isExplicitLoginIntent) {
      const storedUser = localStorage.getItem('timeLockUser');
      if (storedUser && isDemoUser(JSON.parse(storedUser))) {
        clearStoredSession();
      }
      window.history.replaceState({}, document.title, location.pathname);
    }

    // Check if user is logged in (token in localStorage)
    const token = localStorage.getItem('timeLockToken');
    const userData = localStorage.getItem('timeLockUser');
    if (token && userData) {
      const parsedUser = normalizeDemoUser(JSON.parse(userData));
      if (isDemoSessionExpired(parsedUser)) {
        clearStoredSession();
        setIsLoggedIn(false);
        setUser(null);
        setIsAdminMode(false);
        return;
      }

      localStorage.setItem('timeLockUser', JSON.stringify(parsedUser));
      setIsLoggedIn(true);
      setUser(parsedUser);
      // Auto-detect if admin
      setIsAdminMode(parsedUser.admin === 1);

      if (!parsedUser.isDemo) {
        axios
          .get('/api/me', getAuthConfig())
          .then((response) => {
            if (!isMounted || !response.data?.success || !response.data?.user) {
              return;
            }

            const syncedUser = normalizeDemoUser(response.data.user);
            localStorage.setItem('timeLockUser', JSON.stringify(syncedUser));
            setUser(syncedUser);
            setIsAdminMode(syncedUser.admin === 1);
          })
          .catch((error) => {
            console.error('Failed to sync current user:', error);
          });
      }
    }

    return () => {
      isMounted = false;
    };
  }, [location.search]);

  const handleLogin = (loginUser) => {
    const normalizedUser = normalizeDemoUser(loginUser);
    localStorage.setItem('timeLockUser', JSON.stringify(normalizedUser));
    setUser(normalizedUser);
    setIsLoggedIn(true);
    setIsAdminMode(normalizedUser.admin === 1);
  };

  const handleLogout = () => {
    clearStoredSession();
    setIsLoggedIn(false);
    setUser(null);
    setIsAdminMode(false);
  };

  // Check if viewing admin login page (unauthenticated OR admin mode but no token)
  const adminToken = localStorage.getItem('adminToken');
  if (location.pathname === '/admin' && (!isLoggedIn || (isLoggedIn && user?.admin === 1 && !adminToken))) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  // Public message viewer - no login required
  const isViewingMessage = location.pathname.startsWith('/view/');
  const isViewingEvent = location.pathname.startsWith('/event/');

  if (isViewingMessage) {
    const token = location.pathname.split('/view/')[1];
    return <MessageViewer token={token} />;
  }

  if (isViewingEvent) {
    const token = location.pathname.split('/event/')[1];
    return <RecipientEventView token={token} />;
  }

  return (
    <div className="app">
      {isLoggedIn ? (
        <>
          <Dashboard key={user?.id || user?.email} user={user} onLogout={handleLogout} />
          {/* Global Support Agent - Available on all pages */}
          <SupportAgent 
            user={user}
            agentOpen={agentOpen}
            setAgentOpen={setAgentOpen}
            agentMessages={agentMessages}
            setAgentMessages={setAgentMessages}
            agentInput={agentInput}
            setAgentInput={setAgentInput}
            agentLoading={agentLoading}
            setAgentLoading={setAgentLoading}
          />
        </>
      ) : (
        <Login onLogin={handleLogin} />
      )}
    </div>
  );
}

function App() {
  // Shareable links (/view/<token>, /event/<token>) are generated WITHOUT the
  // /app prefix on purpose (shorter URLs for recipients). They're served by
  // the same app bundle, so the basename must adapt to which kind of URL
  // is currently open — otherwise the Router can't match it and renders nothing.
  const basename = window.location.pathname.startsWith('/app') ? '/app' : '/';

  return (
    <Router basename={basename}>
      <Routes>
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;
